import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { IssueInsertType } from '../../types/state/issues';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

type ValidateCustomerPartIdParams = {
  customerPartId: string | null;
  quoteLineUuid: string;
};

export const CUSTOMER_PART_ID_VISUAL_MAX_LENGTH = 30;

export const buildCustomerPartIdIssueId = (quoteLineUuid: string) =>
  buildIssueId({
    area: 'quoteLine',
    field: 'customerPartId',
    quoteLineUuid,
    type: 'invalid',
  });

export const customerPartIdValidator = z
  .string()
  .max(CUSTOMER_PART_ID_VISUAL_MAX_LENGTH, {
    message: `Customer Part ID must be ${CUSTOMER_PART_ID_VISUAL_MAX_LENGTH} characters or less`,
  });

export const configuredCustomerPartIdValidator =
  customerPartIdValidator.nonempty(
    'Customer Part ID is required for newly configured parts.',
  );

export function buildCustomerPartIdIssue(
  quoteLineUuid: string,
  description: string,
): IssueInsertType {
  return {
    title: 'Invalid customer part ID',
    description,
    severity: 'error',
    type: 'invalid',
    field: 'customerPartId',
    area: 'quoteLine',
    quoteLineUuid,
    action: {
      type: 'modify',
      elementId: buildElementId({
        uuid: quoteLineUuid,
        field: 'customerPartId',
      }),
    },
  };
}

export function validateCustomerPartId(
  state: WritableQuoteBuilderStateType,
  { customerPartId, quoteLineUuid }: ValidateCustomerPartIdParams,
) {
  const invalidIssueId = buildCustomerPartIdIssueId(quoteLineUuid);

  const isConfiguredPart = Boolean(
    state.quoteLines[quoteLineUuid].configuratorSnapshot,
  );

  const result = isConfiguredPart
    ? configuredCustomerPartIdValidator.safeParse(customerPartId ?? '')
    : customerPartIdValidator.safeParse(customerPartId ?? '');

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    upsertIssue(
      state,
      invalidIssueId,
      buildCustomerPartIdIssue(quoteLineUuid, error),
    );
    return customerPartId;
  }

  deleteIssue(state, invalidIssueId);

  return result.data;
}
